import { ImageOff } from 'lucide-react';
import type { AdminTrainingPhoto } from 'app/javascript/types/admin';
import { Badge } from 'app/javascript/components/ui/badge';
import { formatBrDate } from 'app/javascript/utils/dates';

const STATUS_LABELS: Record<string, string> = {
  processing: 'processando',
  completed: 'processada',
  failed: 'falhou',
  confirmed: 'confirmada',
};

export default function TrainingPhotoCard({ photo }: { photo: AdminTrainingPhoto }) {
  const thumbnail = photo.photo_thumbnail_url ?? photo.photo_url;
  const dateLabel = photo.class_date ? formatBrDate(photo.class_date) : 'data desconhecida';

  return (
    <figure className="overflow-hidden rounded-lg border bg-card">
      {thumbnail ? (
        // Opens the full-size image straight from ActiveStorage — the debug page
        // has no lightbox of its own.
        <a
          href={photo.photo_url ?? thumbnail}
          target="_blank"
          rel="noreferrer noopener"
          className="block focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring"
        >
          <img
            src={thumbnail}
            alt={`Foto do treino de ${dateLabel}`}
            loading="lazy"
            className="aspect-square w-full object-cover transition-opacity duration-200 hover:opacity-85"
          />
        </a>
      ) : (
        <div className="flex aspect-square w-full flex-col items-center justify-center gap-1.5 bg-secondary/40 text-muted-foreground">
          <ImageOff className="size-6" />
          <span className="text-xs">{photo.attachments_purged ? 'foto expurgada' : 'sem foto'}</span>
        </div>
      )}
      <figcaption className="space-y-1 p-3">
        <div className="flex items-center justify-between gap-2">
          <span className="text-sm font-medium">
            {photo.class_date ? formatBrDate(photo.class_date) : '—'}
            {photo.class_time && <span className="text-muted-foreground"> · {photo.class_time}</span>}
          </span>
          <Badge variant={photo.status === 'failed' ? 'destructive' : 'secondary'}>
            {STATUS_LABELS[photo.status] ?? photo.status}
          </Badge>
        </div>
        <p className="text-xs tabular-nums text-muted-foreground">
          {photo.total_faces ?? 0} rostos · {photo.identified_count ?? 0} reconhecidos ·{' '}
          {photo.check_ins_count} check-ins
        </p>
        {photo.failure_reason && <p className="text-xs text-destructive">{photo.failure_reason}</p>}
      </figcaption>
    </figure>
  );
}
